'use client'

import React from 'react'
import { Panel } from './ui/layout'

interface VisualizerControlsPanelProps {
  visualizerMode: 'waveform' | 'spectrum' | 'neural' | 'particles' | 'spatial'
  onModeChange: (mode: 'waveform' | 'spectrum' | 'neural' | 'particles' | 'spatial') => void
}

const VisualizerControlsPanel: React.FC<VisualizerControlsPanelProps> = ({
  visualizerMode,
  onModeChange
}) => {
  const visualizerOptions = [
    { mode: 'waveform', icon: '🌊', name: 'Waveform', desc: 'Classic wave display', shortcut: '1' },
    { mode: 'spectrum', icon: '📊', name: 'Spectrum', desc: 'Frequency analysis', shortcut: '2' },
    { mode: 'neural', icon: '🧠', name: 'Neural', desc: 'Brain activity', shortcut: '3' },
    { mode: 'particles', icon: '✨', name: 'Particles', desc: 'Particle system', shortcut: '4' },
    { mode: 'spatial', icon: '🌐', name: 'Spatial', desc: '3D spatial view', shortcut: '5' }
  ] as const

  return (
    <Panel title="Visualization Engine" icon="👁️" collapsible={true}>
      <div
        className="space-y-1"
        role="radiogroup"
        aria-label="Visualization mode selection"
        aria-describedby="visualizer-description"
      >
        <div id="visualizer-description" className="sr-only">
          Choose a visualization mode to display audio data. Use arrow keys to navigate and Enter to select.
        </div>
        {visualizerOptions.map(({ mode: visMode, icon, name, desc, shortcut }, index) => {
          const isSelected = visualizerMode === visMode

          return (
            <button
              key={visMode}
              onClick={() => onModeChange(visMode)}
              onKeyDown={(e) => {
                if (e.key === 'ArrowDown' && index < 4) {
                  e.preventDefault()
                  const nextButton = e.currentTarget.nextElementSibling as HTMLElement
                  nextButton?.focus()
                } else if (e.key === 'ArrowUp' && index > 0) {
                  e.preventDefault()
                  const prevButton = e.currentTarget.previousElementSibling as HTMLElement
                  prevButton?.focus()
                } else if (e.key >= '1' && e.key <= '5') {
                  e.preventDefault()
                  const targetIndex = parseInt(e.key) - 1
                  if (targetIndex >= 0 && targetIndex < 5) {
                    const modes = ['waveform', 'spectrum', 'neural', 'particles', 'spatial'] as const
                    onModeChange(modes[targetIndex])
                  }
                }
              }}
              role="radio"
              aria-checked={isSelected ? 'true' : 'false'}
              aria-label={`${name} visualization mode: ${desc}. Shortcut: ${shortcut}`}
              aria-current={isSelected ? 'page' : undefined}
              tabIndex={isSelected ? 0 : -1}
              className={`w-full flex items-center space-x-3 p-3 rounded-lg transition-all duration-200 text-left group focus:outline-none focus:ring-2 focus:ring-cyan-400/50 focus:ring-offset-2 focus:ring-offset-slate-800 ${
                isSelected
                  ? 'bg-gradient-to-r from-cyan-500/20 to-blue-500/20 text-cyan-300 border border-cyan-500/30 shadow-lg shadow-cyan-500/20'
                  : 'hover:bg-slate-700/50 text-slate-300 hover:border-slate-600/50 border border-transparent'
              }`}
            >
              <div className={`flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center transition-all duration-200 ${
                isSelected
                  ? 'bg-cyan-500/20 text-cyan-300'
                  : 'bg-slate-700/50 text-slate-400 group-hover:bg-slate-600/50'
              }`}>
                <span className="text-sm">{icon}</span>
              </div>
              <div className="flex-1 min-w-0">
                <div className={`font-medium text-sm ${isSelected ? 'text-cyan-300' : 'text-slate-200'}`}>
                  {name}
                </div>
                <div className={`text-xs ${isSelected ? 'text-cyan-400' : 'text-slate-400'}`}>
                  {desc}
                </div>
              </div>
            </button>
          )
        })}
      </div>
    </Panel>
  )
}

export default VisualizerControlsPanel